import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS, HERO_IMG, FABRICS } from "../data/content";
import { MaskedLine, Reveal } from "../components/Reveal";
import { MarqueeStrip } from "../components/MarqueeStrip";

export default function Home() {
  const { lang, t } = useLanguage();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 110]);
  const ghostX = useTransform(scrollY, [0, 700], [0, -60]);
  const hindi = lang === "hi";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
        <motion.span
          style={{ x: ghostX }}
          className="text-outline-maroon pointer-events-none absolute -top-4 left-0 select-none font-display text-[26vw] font-bold leading-none"
          aria-hidden="true"
        >
          HB
        </motion.span>

        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-28">
          <div className="flex flex-col justify-end lg:col-span-7 lg:pb-10 lg:pt-32">
            <MaskedLine delay={0.15}>
              <span className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold ${hindi ? "font-hindi" : ""}`}>
                <MapPin size={12} /> {t.hero.kicker}
              </span>
            </MaskedLine>

            <h1 className={`mt-6 leading-[0.95] text-maroon ${hindi ? "font-hindi text-5xl sm:text-7xl lg:text-8xl" : "font-display text-6xl sm:text-8xl lg:text-9xl"}`} data-testid="hero-title">
              <MaskedLine delay={0.3}>{t.hero.line1}</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="italic text-ink">{t.hero.line2}</span>
              </MaskedLine>
            </h1>

            <MaskedLine delay={0.6} className="mt-6">
              <span className={`inline-block border-y border-gold/50 py-2 text-sm tracking-[0.2em] text-maroon ${hindi ? "font-hindi" : "uppercase"}`} data-testid="hero-tagline">
                {hindi ? BUSINESS.tagHi : BUSINESS.tagEn}
              </span>
            </MaskedLine>

            <Reveal delay={0.75}>
              <p className={`mt-6 max-w-md text-base leading-relaxed text-ink/60 sm:text-lg ${hindi ? "font-hindi" : ""}`} data-testid="hero-subtitle">
                {t.hero.sub}
              </p>
            </Reveal>

            <Reveal delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/fabrics"
                data-testid="hero-explore-fabrics-button"
                className={`group flex items-center gap-3 rounded-full bg-maroon px-7 py-4 text-sm font-semibold tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark ${hindi ? "font-hindi" : ""}`}
              >
                {t.hero.cta1}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-visit-shop-button"
                className={`rounded-full border border-maroon/30 px-7 py-4 text-sm font-semibold tracking-wide text-maroon transition-colors duration-300 hover:border-gold hover:bg-gold/10 ${hindi ? "font-hindi" : ""}`}
              >
                {t.hero.cta2}
              </Link>
            </Reveal>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="img-print overflow-hidden rounded-t-[10rem] rounded-b-2xl border border-gold/40">
                <motion.img
                  src={HERO_IMG}
                  alt={hindi ? "रेशमी कपड़ा" : "Premium cloth"}
                  style={{ y: imgY }}
                  className="h-[26rem] w-full scale-110 object-cover sm:h-[34rem]"
                  data-testid="hero-image"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1 }}
                className="absolute -left-4 bottom-8 rounded-xl border border-gold/40 bg-white/85 px-5 py-4 shadow-lg backdrop-blur-md sm:-left-10"
                data-testid="hero-badge"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">{t.hero.badge}</p>
                <p className={`mt-1 text-sm font-semibold text-maroon ${hindi ? "font-hindi" : "font-display"}`}>{t.hero.since}</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      {/* CHAPTERS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="chapters-section">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.chapters.kicker}</p>
          <h2 className={`mt-4 max-w-2xl text-4xl leading-tight text-ink sm:text-5xl ${hindi ? "font-hindi" : "font-display"}`} data-testid="chapters-title">
            {t.chapters.title}
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-gold/30 bg-gold/30 md:grid-cols-3">
          {t.chapters.items.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.15} className="bg-ivory">
              <div className="group flex h-full flex-col p-10 transition-colors duration-500 hover:bg-white">
                <span className="font-display text-6xl font-semibold text-maroon/15 transition-colors duration-500 group-hover:text-gold/60">
                  {c.n}
                </span>
                <h3 className={`mt-6 text-2xl text-maroon ${hindi ? "font-hindi" : "font-display"}`} data-testid={`chapter-title-${c.n}`}>{c.title}</h3>
                <p className={`mt-4 text-sm leading-relaxed text-ink/60 ${hindi ? "font-hindi" : ""}`}>{c.text}</p>
                <span className="mt-auto pt-8">
                  <span className="block h-px w-10 bg-gold transition-all duration-500 group-hover:w-24" />
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="bg-white py-24 sm:py-32" data-testid="gallery-preview-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.gallery.kicker}</p>
              <h2 className={`mt-4 text-4xl leading-tight text-ink sm:text-5xl ${hindi ? "font-hindi" : "font-display"}`} data-testid="gallery-preview-title">
                {t.gallery.title}
              </h2>
              <p className={`mt-4 max-w-md text-sm text-ink/55 ${hindi ? "font-hindi" : ""}`}>{t.gallery.text}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/fabrics"
                data-testid="gallery-view-all-link"
                className={`group flex items-center gap-2 border-b border-maroon pb-1 text-sm font-semibold text-maroon transition-colors duration-300 hover:border-gold ${hindi ? "font-hindi" : ""}`}
              >
                {t.gallery.cta}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {FABRICS.slice(0, 4).map((f, i) => (
              <Reveal key={f.id} delay={i * 0.12} className={i % 2 === 1 ? "sm:mt-12" : ""}>
                <Link to="/fabrics" className="group block" data-testid={`gallery-preview-item-${f.id}`}>
                  <div className="img-print overflow-hidden rounded-2xl border border-gold/25">
                    <img
                      src={f.img}
                      alt={hindi ? f.nameHi : f.nameEn}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <p className={`mt-3 text-sm font-semibold text-ink ${hindi ? "font-hindi" : "font-display text-base"}`}>
                    {hindi ? f.nameHi : f.nameEn}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-maroon py-24 sm:py-28" data-testid="cta-section">
        <span className="pointer-events-none absolute -bottom-10 right-6 select-none font-display text-[18vw] font-bold leading-none text-white/5" aria-hidden="true">
          HB
        </span>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className={`max-w-2xl text-4xl leading-tight text-ivory sm:text-5xl ${hindi ? "font-hindi" : "font-display"}`} data-testid="cta-title">
              {t.cta.title}
            </h2>
            <p className={`mt-5 max-w-xl text-base text-ivory/70 ${hindi ? "font-hindi" : ""}`}>{t.cta.text}</p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className={`group mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-bold tracking-wide text-maroon-dark transition-colors duration-300 hover:bg-gold-soft ${hindi ? "font-hindi" : ""}`}
            >
              {t.cta.btn}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
